<template>
  <div class="gift-card">
    <div class="gift-card-image">
    <div
      v-if="cardImageArray.length > 1"
      class="slider-container"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @mousedown="onTouchStart"
      @mousemove="onTouchMove"
      @mouseup="onTouchEnd"
      @mouseleave="onTouchEnd"
    >
      <img
        :src="cardImageArray[currentImageIndex]"
        :alt="`Gift Card Image ${currentImageIndex + 1}`"
        class="slider-image"
        :style="{ opacity: imageOpacity }"
      />
      <div class="slider-dots">
        <span
          v-for="(img, idx) in cardImageArray"
          :key="'dot-' + idx"
          :class="['slider-dot', { active: currentImageIndex === idx }]"
          @click="goToImage(idx)"
          @touchstart="dotTouchStart($event, idx)"
          @touchend="dotTouchEnd($event, idx)"
          @touchmove="onTouchMove"
        ></span>
      </div>
    </div>
    <img
      v-else
      :src="cardImageSrc"
      alt="Gift Card Image"
      class="slider-image"
    />
  methods: {
    ...existing code...
    onImageError(img, idx) {
      console.error('Image failed to load:', img, 'at index', idx);
    },
  </div>
    <div class="gift-card-content" @click="navigateToGiftCardPage">
      <div class="business-title-row">
        <div class="business-name">{{ businessName}}</div>
        <div class="rating-wrapper">
          <span class="star">★</span>
          <span class="rating-number">{{ rating }}</span>
        </div>
      </div>
      <div class="bussiness-address">{{ businessAddress }}</div>
      <h3 class="deal-name">{{ dealTitle }}</h3>
      <div class="price-wrapper">
        <span class="original-price">${{ originalPrice }}</span>
        <span class="current-price">${{ calculateDiscountedPrice(originalPrice, discountPercentage) }}</span>
        <span class="discount">{{ discountPercentage.toFixed(2) }}% OFF</span>
      </div>
      <div v-if="couponDiscountedPrice && activeCouponCode" class="coupon-discount">
        <span class="coupon-price">${{ couponDiscountedPrice }}</span>
        <span class="coupon-code-text">with code {{ activeCouponCode }}</span>
      </div>
<!--       <div v-if="showButton" class="button-wrapper">
        <button @click="handleButtonClick">{{ buttonText }}</button>
        <div v-if="errorMessage[0]" class="error-message">{{ errorMessage[0] }}</div>
      </div> -->
      <div v-if="isNewItem" class="new-item-badge">
        <span class="star-icon">⭐</span>
        <span>Newly Added to StarkShopper</span>
      </div>
      <div v-else-if="hasNewInventory" class="new-inventory-badge">
        <span class="plus-icon">➕</span>
        <span>More Inventory Added</span>
      </div>
      <div v-else-if="isLowStock" class="low-stock-warning">
        <span class="hot-icon">🔥</span>
        <span>Almost out of Stock</span>
      </div>
    </div>
  </div>
</template>

<script>
import defaultImage from '@/assets/default-image.png';
import axios from 'axios';
import NavigationTracker from '../../utils/navigationTracker.js';

// Global batch manager for fetching discounts
const discountBatchManager = {
  pendingGiftCards: new Set(),
  callbacks: {},
  batchTimeout: null,
  batchDelay: 100, // Wait 100ms to collect all gift card IDs
  cache: {},
  cacheExpiry: 5 * 60 * 1000, // 5 minutes

  request(giftCardId, callback) {
    // Check cache first
    const cached = this.cache[giftCardId];
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      callback(cached.data);
      return;
    }

    // Add to pending batch
    this.pendingGiftCards.add(giftCardId);
    if (!this.callbacks[giftCardId]) {
      this.callbacks[giftCardId] = [];
    }
    this.callbacks[giftCardId].push(callback);

    // Schedule batch request
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
    this.batchTimeout = setTimeout(() => this.executeBatch(), this.batchDelay);
  },

  async executeBatch() {
    const giftCardIds = Array.from(this.pendingGiftCards);
    const callbacks = { ...this.callbacks };

    // Clear pending
    this.pendingGiftCards.clear();
    this.callbacks = {};
    this.batchTimeout = null;

    if (giftCardIds.length === 0) return;

    try {
      const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/discounts/gift-cards/batch`, {
        giftCardIds
      });

      const discountsByGiftCard = response.data;

      // Call callbacks and cache results
      giftCardIds.forEach(giftCardId => {
        const discounts = discountsByGiftCard[giftCardId] || [];

        // Cache the result
        this.cache[giftCardId] = {
          data: discounts,
          timestamp: Date.now()
        };

        // Call all callbacks for this gift card
        if (callbacks[giftCardId]) {
          callbacks[giftCardId].forEach(cb => cb(discounts));
        }
      });
    } catch (error) {
      console.error('Error fetching batch discounts:', error);

      // Call callbacks with empty array on error
      giftCardIds.forEach(giftCardId => {
        if (callbacks[giftCardId]) {
          callbacks[giftCardId].forEach(cb => cb([]));
        }
      });
    }
  }
};

export default {
  name: 'GiftCard',
  props: {
    giftCardId: {
      type: String,
      required: true
    },
    businessName: {
      type: String,
      required: true
    },
    dealTitle: {
      type: String,
      required: true
    },
    rating: {
      type: Number,
      required: true
    },
    cardImage: {
      type: [String, Array],
      default: ''
    },
    originalPrice: {
      type: Number,
      required: true
    },
    discountPercentage: {
      type: Array,
      required: true
    },
    businessId: {
      type: String,
      required: true
    },
    businessAddress: {
      type: String,
      default: ''
    },
    optionsCount: {
      type: Number,
      default: 1
    },
    numberinstock: {
      type: Number,
      default: 999
    },
    isNewItem: {
      type: Boolean,
      default: false
    },
    hasNewInventory: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isInCart: false,
      message: '',
      fading: false,
      isLoggedIn: false,
      token: localStorage.getItem('token'),
      showButton: this.shouldShowButton(),
      optionQuantities: {},
      cartStatuses: {},
      alreadyPurchasedMessage: {},
      errorMessage: {},
      currentImageIndex: 0,
      // Swipe/drag logic
      startX: 0,
      dragging: false,
      imageOpacity: 1, // For fade animation
      activeCoupons: [], // Store active discount codes for this gift card
      isFetchingCoupons: false, // Prevent duplicate fetches
    };
  },
  computed: {
    cardImageArray() {
      // If cardImage is an array, filter out empty/invalid URLs
      if (Array.isArray(this.cardImage)) {
        const arr = this.cardImage.filter(url => typeof url === 'string' && url.trim() !== '');
        return arr.length ? arr : [defaultImage];
      }
      // If cardImage is a string, handle comma-separated and Postgres curly-brace formats
      if (typeof this.cardImage === 'string' && this.cardImage.trim() !== '') {
        let str = this.cardImage.trim();
        // Remove curly braces if present
        if (str.startsWith('{') && str.endsWith('}')) {
          str = str.slice(1, -1);
        }
        const arr = str.split(',').map(url => url.trim()).filter(url => url !== '');
        return arr.length ? arr : [defaultImage];
      }
      // Fallback to default image
      return [defaultImage];
    },
    cardImageSrc() {
      // For backward compatibility, return first image
      return this.cardImageArray[0];
    },
    buttonText() {
      if (this.isInCart) {
        return 'View Cart';
      }
      return this.optionsCount > 1 ? 'View More Options' : 'Add to Cart';
    },
    stockCount() {
      // Handle both cases: when numberinstock is an array or a single value
      return Array.isArray(this.numberinstock) ? this.numberinstock[0] : this.numberinstock;
    },
    isLowStock() {
      return this.stockCount < 10 && this.stockCount > 0;
    },
    couponDiscountedPrice() {
      if (this.activeCoupons.length === 0) return null;

      // Get the first active coupon (assuming we only show one at a time)
      const coupon = this.activeCoupons[0];
      const currentPrice = parseFloat(this.calculateDiscountedPrice(this.originalPrice, this.discountPercentage));

      // Apply the coupon discount percentage to the current discounted price
      const couponDiscount = currentPrice * (coupon.discount_percentage / 100);
      return (currentPrice - couponDiscount).toFixed(2);
    },
    activeCouponCode() {
      if (this.activeCoupons.length === 0) return null;
      return this.activeCoupons[0].code;
    }
  },
  methods: {
    // For image area
    onTouchStart(e) {
      e.stopPropagation();
      e.preventDefault();
      this.dragging = true;
      this.startX = e.touches ? e.touches[0].clientX : e.clientX;
    },
    // For dots: distinguish tap vs swipe
    dotTouchStart(e, idx) {
      this.dotTouch = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        time: Date.now(),
        idx
      };
      // Also allow swipe to start from dot
      this.onTouchStart(e);
    },
    dotTouchEnd(e, idx) {
      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.clientX - this.dotTouch.startX);
      const dy = Math.abs(touch.clientY - this.dotTouch.startY);
      const dt = Date.now() - this.dotTouch.time;
      // Consider as tap if movement is small and quick
      if (dx < 10 && dy < 10 && dt < 300) {
        this.goToImage(idx);
      }
      this.onTouchEnd(e);
    },
    onTouchMove(e) {
      e.stopPropagation();
      e.preventDefault();
      if (!this.dragging) return;
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      const diff = currentX - this.startX;
      // Only trigger if swipe is significant
      if (Math.abs(diff) > 40) {
        if (diff < 0) {
          this.nextImage();
        } else {
          this.prevImage();
        }
        this.dragging = false;
      }
    },
    onTouchEnd(e) {
      e.stopPropagation();
      e.preventDefault();
      this.dragging = false;
    },
    animateImageChange(newIndex) {
      this.imageOpacity = 0;
      setTimeout(() => {
        this.currentImageIndex = newIndex;
        this.imageOpacity = 1;
      }, 200);
    },
    prevImage() {
      if (this.cardImageArray.length > 1) {
        this.animateImageChange((this.currentImageIndex - 1 + this.cardImageArray.length) % this.cardImageArray.length);
      }
    },
    nextImage() {
      if (this.cardImageArray.length > 1) {
        this.animateImageChange((this.currentImageIndex + 1) % this.cardImageArray.length);
      }
    },
    goToImage(idx) {
      if (this.cardImageArray.length > 1) {
        this.animateImageChange(idx);
      }
    },
    navigateToGiftCardPage() {
      // Track navigation based on current page context
      const currentPath = this.$route.path;
      
      if (currentPath === '/gift-cards') {
        // Get category from URL if available
        const urlParams = new URLSearchParams(window.location.search);
        const category = urlParams.get('category') || 'all';
        NavigationTracker.trackFromGiftCards(category);
      } else if (currentPath.startsWith('/business/')) {
        NavigationTracker.trackFromBusinessProfile(this.businessId, this.businessName);
      } else if (currentPath === '/search') {
        NavigationTracker.trackFromSearch(this.$route.query.query || '');
      } else if (currentPath === '/incentives') {
        NavigationTracker.trackFromIncentives();
      } else if (currentPath === '/cart') {
        NavigationTracker.trackFromCart();
      } else if (currentPath === '/checkout') {
        NavigationTracker.trackFromCheckout();
      } else if (currentPath === '/') {
        NavigationTracker.trackFromHome();
      } else {
        // Default to related deals context
        NavigationTracker.trackFromRelatedDeals();
      }
      
      this.$router.push(`/gift-card/${this.giftCardId}`).then(() => {
        window.location.reload();
        window.scrollTo(0, 0);
      });
    },
    calculateDiscountedPrice(originalPrice, discountPercentage) {
      return (originalPrice * (1 - discountPercentage / 100)).toFixed(2);
    },
    getStarClass(index) {
      if (this.rating === 0) {
        return 'blank';
      } else if (index <= Math.floor(this.rating)) {
        return 'filled';
      } else if (index === Math.ceil(this.rating) && this.rating % 1 !== 0) {
        return 'half-filled';
      } else {
        return 'blank';
      }
    },
    async handleButtonClick(event) {
      event.stopPropagation();
      if (this.isInCart) {
        this.$router.push('/cart');
      } else if (this.optionsCount > 1) {
        // Track navigation before going to gift card page
        const currentPath = this.$route.path;
        
        if (currentPath === '/gift-cards') {
          const urlParams = new URLSearchParams(window.location.search);
          const category = urlParams.get('category') || 'all';
          NavigationTracker.trackFromGiftCards(category);
        } else if (currentPath.startsWith('/business/')) {
          NavigationTracker.trackFromBusinessProfile(this.businessId, this.businessName);
        } else if (currentPath === '/search') {
          NavigationTracker.trackFromSearch(this.$route.query.query || '');
        } else if (currentPath === '/incentives') {
          NavigationTracker.trackFromIncentives();
        } else if (currentPath === '/cart') {
          NavigationTracker.trackFromCart();
        } else if (currentPath === '/checkout') {
          NavigationTracker.trackFromCheckout();
        } else if (currentPath === '/') {
          NavigationTracker.trackFromHome();
        } else {
          NavigationTracker.trackFromRelatedDeals();
        }
        
        this.$router.push(`/gift-card/${this.giftCardId}`);
        window.location.reload();
      } else {
        const success = await this.addToCart(0);
        if (success) {
          this.$router.push('/cart');
        } else {
          this.showMessage('You have already purchased this gift card.');
        }
      }
    },
    async addToCart(optionId) {
      if (this.cartStatuses[optionId]) {
        this.showMessage('This option is already in the cart');
        return false;
      } else {
        return await this.saveGiftCard(optionId);
      }
    },
    async saveGiftCard(optionId) {
      const cartItemIdentifier = `${this.giftCardId}-${optionId}`;
      let checkoutCartItems = JSON.parse(localStorage.getItem('checkoutCartItems')) || [];

      if (checkoutCartItems.some(item => item.cartItemId === cartItemIdentifier)) {
        this.showMessage('This option is already in the cart');
        return false;
      }

      const quantity = this.optionQuantities[optionId] || 1;
      const newItem = {
        cartItemId: cartItemIdentifier,
        giftCardId: this.giftCardId,
        optionId: optionId,
        dealTitle: this.dealTitle,
        originalPrice: this.originalPrice,
        discount: this.discountPercentage,
        quantity: quantity,
        businessId: this.businessId,
        name: this.dealTitle,
        image_link: this.cardImageSrc,
        businessName: this.businessName,
        businessAddress: this.businessAddress,
        rating: this.rating
      };

      if (this.isLoggedIn) {
        const userId = JSON.parse(localStorage.getItem('user')).id;
        const token = localStorage.getItem('token');
        
        try {
          console.log("Adding to cart with businessId:", this.businessId); // Add logging for debugging
          await axios.post(`${process.env.VUE_APP_API_URL}/api/shoppingCart/saveCart/${userId}`, newItem, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

          checkoutCartItems.push(newItem);
          localStorage.setItem('checkoutCartItems', JSON.stringify(checkoutCartItems));
          window.dispatchEvent(new CustomEvent('update-checkout-cart', { detail: checkoutCartItems }));
          this.cartStatuses[optionId] = true;
          return true;

        } catch (error) {
          console.error("Axios Error:", error);
          console.error("Failed cart item data:", newItem); // Log the failed item data

          if (error.response && error.response.status === 400) {
            this.errorMessage = { ...this.errorMessage, [optionId]: 'You have already purchased this gift card and cannot buy another one.' };
          } else {
            this.errorMessage = { ...this.errorMessage, [optionId]: 'Sorry, an error occurred while adding to cart.' };
          }

          setTimeout(() => {
            this.errorMessage = { ...this.errorMessage, [optionId]: '' };
          }, 3000);

          return false;
        }
      } else {
        checkoutCartItems.push(newItem);
        localStorage.setItem('checkoutCartItems', JSON.stringify(checkoutCartItems));
        window.dispatchEvent(new CustomEvent('update-checkout-cart', { detail: checkoutCartItems }));
        this.cartStatuses[optionId] = true;
        return true;
      }
    },
    async removeFromCart(optionId) {
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user ? user.id : null;
      const cartItemIdentifier = `${this.giftCardId}-${optionId}`;

      if (userId) {
        try {
          await axios.put(`${process.env.VUE_APP_API_URL}/api/shoppingCart/remove-from-cart/${userId}`, {
            giftCardId: this.giftCardId,
            name: this.dealTitle
          }, {
            headers: { 'Authorization': `Bearer ${this.token}` }
          });
        } catch (error) {
          console.error('Error removing from cart on server:', error);
        }
      }

      let checkoutCartItems = JSON.parse(localStorage.getItem('checkoutCartItems')) || [];
      checkoutCartItems = checkoutCartItems.filter(item => item.cartItemId !== cartItemIdentifier);
      localStorage.setItem('checkoutCartItems', JSON.stringify(checkoutCartItems));
      window.dispatchEvent(new CustomEvent('update-checkout-cart', { detail: checkoutCartItems }));
      this.showMessage('Gift Card Removed from Cart');
      this.cartStatuses[optionId] = false;
    },
    showMessage(text) {
      this.message = text;
      this.fading = false;
      setTimeout(() => {
        this.fading = true;
        setTimeout(() => {
          this.message = '';
          this.fading = false;
        }, 1000);
      }, 3000);
    },
    shouldShowButton() {
      let abGroup = localStorage.getItem('abTestGroup');

      if (!abGroup) {
        // Get user ID if logged in
        const user = JSON.parse(localStorage.getItem('user'));
        let userId;
        
        if (user && user.id) {
          userId = user.id.toString();
        } else {
          // For non-logged in users, get or create a session ID
          userId = localStorage.getItem('sessionToken');
          if (!userId) {
            const array = new Uint32Array(1);
            window.crypto.getRandomValues(array);
            userId = array[0].toString();
            localStorage.setItem('sessionToken', userId);
          }
        }

        // Create a simple hash from the userId string
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
          hash = ((hash << 5) - hash) + userId.charCodeAt(i);
          hash = hash & hash; // Convert to 32bit integer
        }

        // Determine group A or B based on hash
        abGroup = (Math.abs(hash) % 2 === 0) ? 'A' : 'B';
        localStorage.setItem('abTestGroup', abGroup);
      }

      return abGroup === 'A';
    },
    checkIfInCart() {
      const checkoutCartItems = JSON.parse(localStorage.getItem('checkoutCartItems')) || [];
      this.isInCart = checkoutCartItems.some(item => item.giftCardId === this.giftCardId);
    },
    fetchActiveCoupons() {
      // Use batch manager - it will automatically batch requests
      discountBatchManager.request(this.giftCardId, (discounts) => {
        this.activeCoupons = discounts;
      });
    }
  },
  async mounted() {
    if (this.options) {
      this.options.forEach(option => {
        this.optionQuantities[option.id] = 1;
      });
    }
    window.addEventListener('update-checkout-cart', this.checkIfInCart);
    const user = JSON.parse(localStorage.getItem('user'));
    this.isLoggedIn = !!(user && user.id);
    if (user && user.notification_preferences && user.notification_preferences[this.businessId]) {
      const preferences = user.notification_preferences[this.businessId];
      this.isFan = !!preferences.isFan;
      this.subscription = {
        email: preferences.email || false,
        text: preferences.text || false,
        notifications: preferences.notifications || false
      };
    }
    this.checkIfInCart();
    // Fetch discounts using batch manager (prevents rate limiting)
    this.fetchActiveCoupons();
  },
  beforeUnmount() {
    window.removeEventListener('update-checkout-cart', this.checkIfInCart);
  },
}
</script>

<style scoped>
.gift-card {
  border-radius: 8px;
  background: white;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  width: 100%;
  max-width: 270px;
  min-width: 220px;
  flex: 1 0 250px;
  margin: 0;
  font-family: 'Roboto', sans-serif;
  padding-bottom: 12px;
  transition: box-shadow 0.2s, transform 0.2s;
}

@media (min-width: 1400px) {
  .gift-card {
    max-width: 270px;
    min-width: 220px;
    flex: 1 0 250px;
  }
}

@media (max-width: 1200px) {
  .gift-card {
    max-width: 230px;
    min-width: 180px;
    flex: 1 0 200px;
  }
}

@media (max-width: 992px) {
  .gift-card {
    max-width: 180px;
    min-width: 140px;
    flex: 1 0 150px;
  }
}

@media (max-width: 768px) {
  .gift-card {
    max-width: 90vw;
    min-width: 120px;
    flex: 1 0 90vw;
    margin: 0 auto 0 auto;
  }
}

@media (max-width: 480px) {
  .gift-card {
    max-width: 98vw;
    min-width: 100px;
    flex: 1 0 98vw;
    margin: 0 auto 0 auto;
  }
}

.gift-card-image {
  width: 100%;
  background: #f5f5f5;
  position: relative;
  aspect-ratio: 1.61 / 1; /* ~62% aspect ratio */
  overflow: hidden;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.slider-image {
  position: static;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: block;
  border: 1.5px solid #e0e0e0;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  background: #f5f5f5;
}


.slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.7);
  border: none;
  font-size: 22px;
  color: #2E8B57;
  padding: 4px 10px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
}
.slider-arrow.left {
  left: 8px;
}
.slider-arrow.right {
  right: 8px;
}

.slider-dots {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 2;
}
.slider-dot {
  width: 8px;
  height: 8px;
  background: #ccc;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  transition: background 0.2s;
}
.slider-dot.active {
  background: #2E8B57;
}



.gift-card-content {
  padding: 16px 16px 12px 16px;
}

.business-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.business-name {
  font-size: 15px;
  color: #333333;
  font-weight: 700;
  padding-top: 1%;
  font-family: 'Roboto', sans-serif;
  padding-bottom: 2px;
}

.bussiness-address{
  font-size: 13px;
  color: #505050;
  font-weight: 500;
  margin-bottom: 8px;
  font-family: 'Roboto', sans-serif;
}

.rating-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
}

.star {
  color: #FFD700;
  font-size: 18px;
}

.rating-number {
  font-size: 13px;
  color: #333;
  font-weight: 600;
  padding-top:4%;
  padding-left: 2%
}

.deal-name {
  margin: 0 0 14px 0;
  font-size: 25px;
  line-height: 1.1;
  color: #222;
  font-weight: 900;
  font-family: 'Roboto', sans-serif;
  padding-bottom: 12px;
  letter-spacing: -1px;
}

.price-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.original-price {
  color: #666;
  text-decoration: line-through;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
}

.current-price {
  color: #2E8B57;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
}

.discount {
  background-color: #E8F5E9;
  color: #2E8B57;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
}

.coupon-discount {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 0;
  font-family: 'Roboto', sans-serif;
  border-top: 1px dashed #e0e0e0;
}

.coupon-price {
  color: #1976D2;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.coupon-code-text {
  color: #666;
  font-size: 13px;
  font-weight: 500;
  font-style: italic;
}

.button-wrapper {
  text-align: center;
  margin-top: 8px;
}

.error-message {
  color: red;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.button-wrapper button {
  background-color: #2E8B57;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Roboto', sans-serif;
}

.button-wrapper button:hover {
  background-color: #276a46;
}

.new-item-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2E8B57;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 8px;
  text-transform: uppercase;
  border-top: 1px dashed #2E8B57;
  border-bottom: 1px dashed #2E8B57;
  padding: 4px 0;
}

.star-icon {
  margin-right: 6px;
  font-size: 13px;
}

.new-inventory-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1976D2;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 8px;
  text-transform: uppercase;
  border-top: 1px dashed #1976D2;
  border-bottom: 1px dashed #1976D2;
  padding: 4px 0;
}

.plus-icon {
  margin-right: 6px;
  font-size: 13px;
}

.low-stock-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d32f2f;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 8px;
  text-transform: uppercase;
  border-top: 1px dashed #d32f2f;
  border-bottom: 1px dashed #d32f2f;
  padding: 4px 0;
}

.hot-icon {
  margin-right: 6px;
  font-size: 13px;
}

@media (max-width: 1200px) {
  .gift-card {
    flex: 1 0 28%;
    max-width: 28%;
    min-width: 180px;
  }
}

@media (max-width: 992px) {
  .gift-card {
    flex: 1 0 40%;
    max-width: 40%;
    min-width: 160px;
  }
}

@media (max-width: 768px) {
  .gift-card {
    flex: 1 0 48%;
    max-width: 48%;
    min-width: 140px;
    margin: 8px auto;
    margin-bottom: 14px;
  }
  .gift-card-content {
    padding: 8px 8px 6px 8px;
  }
  .deal-name {
    font-size: 25px;
    padding-bottom: 5px;
  }
}

@media (max-width: 480px) {
  .gift-card {
    flex: 1 0 98%;
    max-width: 98%;
    min-width: 120px;
    margin: 6px auto;
    margin-bottom: 10px;
  }
  .gift-card-content {
    padding: 6px 6px 4px 6px;
  }
  .deal-name {
    font-size: 25px;
    padding-bottom: 3px;
  }
}
</style>
